import { useRef, useState } from "react";
import { Image as ImageIcon, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export interface CapturedImage {
  name: string;
  mimeType: string;
  base64: string;
  previewUrl: string;
}

interface Props {
  image: CapturedImage | null;
  onChange: (image: CapturedImage | null) => void;
}

const ACCEPTED = [".png", ".jpg", ".jpeg", "image/png", "image/jpeg"].join(",");

export const ImageCapture = ({ image, onChange }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    if (!/image\/(png|jpe?g)/i.test(file.type)) {
      toast.error("Please upload a .png, .jpg or .jpeg image");
      return;
    }
    setBusy(true);
    try {
      // Phone photos are often 5-10 MB; shrink to max 1600px JPEG so the
      // upload stays small enough for the AI step.
      const objectUrl = URL.createObjectURL(file);
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error("Could not read image"));
        el.src = objectUrl;
      });
      const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Could not read image");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(objectUrl);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
      onChange({
        name: file.name,
        mimeType: "image/jpeg",
        base64: dataUrl.split(",")[1],
        previewUrl: dataUrl,
      });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not read image");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div
        onClick={() => !busy && inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
        className="cursor-pointer rounded-lg border-2 border-dashed border-muted-foreground/30 p-6 text-center transition-colors hover:border-primary/50"
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <div className="flex flex-col items-center gap-2">
          {busy ? <Loader2 className="h-8 w-8 animate-spin text-primary" /> : <ImageIcon className="h-8 w-8 text-muted-foreground" />}
          <p className="text-sm font-medium">{image?.name ?? "Upload a receipt, invoice, note or screenshot"}</p>
          <p className="text-xs text-muted-foreground">Accepted formats: PNG, JPG, JPEG</p>
        </div>
      </div>

      {image && (
        <div className="relative overflow-hidden rounded-lg border">
          <img src={image.previewUrl} alt={`Preview of ${image.name}`} className="max-h-72 w-full object-contain bg-muted/30" />
          <Button
            variant="secondary"
            size="sm"
            className="absolute right-2 top-2"
            onClick={() => onChange(null)}
          >
            <X className="mr-1 h-3 w-3" /> Remove
          </Button>
        </div>
      )}
    </div>
  );
};
